import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Search, Plus, X, Package, Users, DollarSign, CheckSquare, Square } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { useSale, useCreateSale, useUpdateSale } from '../../hooks/useSales';
import { useClients } from '../../hooks/useClients';
import { useProducts } from '../../hooks/useProducts';
import { useDebounce } from '../../hooks/useDebounce';
import { formatCurrency } from '../../lib/utils';
import { z } from 'zod';

const saleFormSchema = z.object({
  clienteId: z.number().nullable().optional(),
  clienteNombre: z.string().min(1, 'El nombre del cliente es requerido'),
  clienteDocumento: z.string().optional().nullable(),
  clienteTelefono: z.string().optional().nullable(),
  clienteEmail: z.string().email('Email inválido').optional().nullable(),
  clienteDireccion: z.string().optional().nullable(),
  clienteLocalidad: z.string().optional().nullable(),
  clienteProvincia: z.string().optional().nullable(),
  clienteCp: z.string().optional().nullable(),
  subtotal: z.number().min(0).default(0),
  descuento: z.number().min(0).max(100).default(0),
  costoEnvio: z.number().min(0).default(0),
  comision: z.number().min(0).max(100).default(0),
  total: z.number().min(0).default(0),
  metodoPago: z.enum(['efectivo', 'transferencia', 'tarjeta', 'cuotas']).default('efectivo'),
  estado: z.enum(['pendiente', 'completada', 'cancelada']).default('pendiente'),
  observaciones: z.string().optional().nullable(),
});

type SaleFormData = z.infer<typeof saleFormSchema>;

interface ProductoSeleccionado {
  id: number;
  nombre: string;
  cantidad: number;
  precio: number;
  subtotal: number;
}

export default function SaleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: venta, isLoading: ventaLoading } = useSale(id ? parseInt(id) : 0);
  const { data: clientsData } = useClients({ limit: 1000 });
  const clients = clientsData?.data ?? [];

  const createSale = useCreateSale();
  const updateSale = useUpdateSale();

  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [autocompletar, setAutocompletar] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [cantidad, setCantidad] = useState(1);
  const [productosSeleccionados, setProductosSeleccionados] = useState<ProductoSeleccionado[]>([]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const shouldSearchProducts =
    searchTerm.trim().length > 0 &&
    (!selectedProduct || selectedProduct.nombre !== searchTerm);

  const { data: productsData, isLoading: loadingProducts } = useProducts({
    search: shouldSearchProducts ? debouncedSearchTerm.trim() : undefined,
    limit: 20,
    activo: true,
  });

  const filteredProducts = productsData?.data ?? [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SaleFormData>({
    resolver: zodResolver(saleFormSchema),
    defaultValues: {
      clienteNombre: '',
      clienteTelefono: '',
      clienteEmail: '',
      clienteDireccion: '',
      clienteDocumento: '',
      clienteLocalidad: '',
      subtotal: 0,
      descuento: 0,
      costoEnvio: 0,
      comision: 0,
      total: 0,
      metodoPago: 'efectivo',
      estado: 'pendiente',
      observaciones: '',
    },
  });

  const subtotal = watch('subtotal') || 0;
  const descuento = watch('descuento') || 0;
  const costoEnvio = watch('costoEnvio') || 0;
  const comision = watch('comision') || 0;

  useEffect(() => {
    if (venta && isEditing) {
      setValue('clienteId', venta.clienteId || null);
      setValue('clienteNombre', venta.clienteNombre);
      setValue('clienteTelefono', venta.clienteTelefono || '');
      setValue('clienteEmail', venta.clienteEmail || '');
      setValue('clienteDireccion', venta.clienteDireccion || '');
      setValue('clienteDocumento', venta.clienteDocumento || '');
      setValue('clienteLocalidad', venta.clienteLocalidad || '');
      setValue('subtotal', Number(venta.subtotal) || 0);
      setValue('descuento', Number(venta.descuento) || 0);
      setValue('costoEnvio', Number(venta.costoEnvio) || 0);
      setValue('comision', Number(venta.comision) || 0);
      setValue('metodoPago', venta.metodoPago || 'efectivo');
      setValue('estado', venta.estado || 'pendiente');
      setValue('observaciones', venta.observaciones || '');

      if (venta.detalles && Array.isArray(venta.detalles)) {
        setProductosSeleccionados(venta.detalles.map((d: any) => ({
          id: d.productoId,
          nombre: d.producto?.nombre || 'Producto',
          cantidad: d.cantidad,
          precio: Number(d.precioUnitario),
          subtotal: Number(d.subtotal),
        })));
      }

      if (venta.clienteId) {
        const cliente = clients.find((c: any) => c.id === venta.clienteId);
        if (cliente) {
          setClienteSeleccionado(cliente);
          setSearchCliente(cliente.nombre);
        }
      }
    }
  }, [venta, isEditing, setValue, clients]);

  useEffect(() => {
    const nuevoSubtotal = productosSeleccionados.reduce((sum, p) => sum + p.subtotal, 0);
    setValue('subtotal', nuevoSubtotal);
  }, [productosSeleccionados, setValue]);

  const handleSelectClient = (client: any) => {
    setClienteSeleccionado(client);
    setSearchCliente(client.nombre);
    setShowClienteDropdown(false);
    setValue('clienteId', client.id);
    setValue('clienteNombre', client.nombre);

    setValue('clienteTelefono', client.telefono || '');
    setValue('clienteEmail', client.email || '');

    if (autocompletar) {
      setValue('clienteDireccion', client.direccion || '');
      setValue('clienteDocumento', client.cuil || client.documento || '');
      setValue('clienteLocalidad', client.localidad || '');
    } else {
      setValue('clienteDireccion', '');
      setValue('clienteDocumento', '');
      setValue('clienteLocalidad', '');
    }

    toast.success(`Cliente seleccionado: ${client.nombre}`);
  };

  const toggleAutocompletar = () => {
    const nuevoEstado = !autocompletar;
    setAutocompletar(nuevoEstado);

    if (nuevoEstado && clienteSeleccionado) {
      setValue('clienteTelefono', clienteSeleccionado.telefono || '');
      setValue('clienteEmail', clienteSeleccionado.email || '');
      setValue('clienteDireccion', clienteSeleccionado.direccion || '');
      setValue('clienteDocumento', clienteSeleccionado.cuil || clienteSeleccionado.documento || '');
      setValue('clienteLocalidad', clienteSeleccionado.localidad || '');
    }
  };

  const handleSelectProduct = (product: any) => {
    setSelectedProduct(product);
    setSearchTerm(product.nombre);
    setShowDropdown(false);
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setCantidad(1);
    setShowDropdown(false);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto primero');
      return;
    }

    if (cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const stockDisponible = selectedProduct.stock || 0;
    if (cantidad > stockDisponible) {
      toast.error(`Stock insuficiente. Disponible: ${stockDisponible}`);
      return;
    }

    const nuevoProducto: ProductoSeleccionado = {
      id: selectedProduct.id,
      nombre: selectedProduct.nombre,
      cantidad,
      precio: Number(selectedProduct.precio) || 0,
      subtotal: cantidad * (Number(selectedProduct.precio) || 0),
    };

    const existe = productosSeleccionados.some(p => p.id === nuevoProducto.id);
    if (existe) {
      toast.error('El producto ya está agregado');
      return;
    }

    setProductosSeleccionados([...productosSeleccionados, nuevoProducto]);
    setSelectedProduct(null);
    setSearchTerm('');
    setCantidad(1);
    setShowDropdown(false);
    toast.success('Producto agregado');
  };

  const handleRemoveProduct = (index: number) => {
    setProductosSeleccionados(productosSeleccionados.filter((_, i) => i !== index));
  };

  const handleCantidadChange = (index: number, newCantidad: number) => {
    if (newCantidad < 1) return;
    const updated = [...productosSeleccionados];
    updated[index].cantidad = newCantidad;
    updated[index].subtotal = newCantidad * updated[index].precio;
    setProductosSeleccionados(updated);
  };

  const filteredClients = clients.filter((c: any) => {
    const searchLower = searchCliente.toLowerCase();
    const nombreMatch = c.nombre?.toLowerCase().includes(searchLower) || false;
    const telefonoMatch = c.telefono?.toLowerCase().includes(searchLower) || false;
    return nombreMatch || telefonoMatch;
  }) || [];

  const isFieldDisabled = !!clienteSeleccionado && autocompletar;

  const onSubmit = async (data: SaleFormData) => {
    try {
      if (productosSeleccionados.length === 0) {
        toast.error('Debe agregar al menos un producto');
        return;
      }

      const subtotalCalculado = productosSeleccionados.reduce((sum, p) => sum + p.subtotal, 0);
      const descuentoPorcentaje = data.descuento || 0;
      const descuentoMonto = subtotalCalculado * (descuentoPorcentaje / 100);
      const costoEnvioCalculado = data.costoEnvio || 0;
      const comisionCalculada = data.comision || 0;
      const totalCalculado = subtotalCalculado - descuentoMonto + costoEnvioCalculado + (subtotalCalculado * (comisionCalculada / 100));

      const payload = {
        clienteId: clienteSeleccionado?.id || null,
        clienteNombre: clienteSeleccionado?.nombre || data.clienteNombre,
        clienteDocumento: clienteSeleccionado?.cuil || clienteSeleccionado?.documento || data.clienteDocumento || null,
        clienteTelefono: clienteSeleccionado?.telefono || data.clienteTelefono || null,
        clienteEmail: clienteSeleccionado?.email || data.clienteEmail || null,
        clienteDireccion: clienteSeleccionado?.direccion || data.clienteDireccion || null,
        clienteLocalidad: clienteSeleccionado?.localidad || data.clienteLocalidad || null,
        clienteProvincia: data.clienteProvincia || null,
        clienteCp: data.clienteCp || null,
        subtotal: subtotalCalculado,
        descuento: descuentoMonto,
        costoEnvio: costoEnvioCalculado,
        comision: comisionCalculada,
        total: totalCalculado,
        metodoPago: data.metodoPago || 'efectivo',
        estado: data.estado || 'pendiente',
        observaciones: data.observaciones || null,
        detalles: productosSeleccionados.map(p => ({
          productoId: p.id,
          cantidad: p.cantidad,
          precioUnitario: p.precio,
          subtotal: p.subtotal,
          total: p.subtotal,
        })),
      };

      if (isEditing && id) {
        await updateSale.mutateAsync({ id: parseInt(id), data: payload });
      } else {
        await createSale.mutateAsync(payload);
      }
      navigate('/ventas');
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstError = errors[0]?.message || 'Error en los datos';
        toast.error(firstError);
      } else {
        toast.error(error.response?.data?.error || 'Error al guardar la venta');
      }
    }
  };

  if (ventaLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando venta...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/ventas')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Venta' : 'Nueva Venta'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Cliente */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users size={20} className="text-blue-500" />
                Datos del Cliente
              </h2>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buscar Cliente Existente
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Buscar cliente por nombre o teléfono..."
                    value={searchCliente}
                    onChange={(e) => {
                      setSearchCliente(e.target.value);
                      setShowClienteDropdown(true);
                      if (!e.target.value) {
                        setClienteSeleccionado(null);
                        setValue('clienteId', null);
                        setValue('clienteNombre', '');
                        setValue('clienteTelefono', '');
                        setValue('clienteEmail', '');
                        setValue('clienteDireccion', '');
                        setValue('clienteDocumento', '');
                        setValue('clienteLocalidad', '');
                      }
                    }}
                    onFocus={() => setShowClienteDropdown(true)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchCliente && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchCliente('');
                        setClienteSeleccionado(null);
                        setValue('clienteId', null);
                        setValue('clienteNombre', '');
                        setValue('clienteTelefono', '');
                        setValue('clienteEmail', '');
                        setValue('clienteDireccion', '');
                        setValue('clienteDocumento', '');
                        setValue('clienteLocalidad', '');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}

                  {/* Dropdown */}
                  {showClienteDropdown && searchCliente && filteredClients.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map((client: any) => (
                        <button
                          key={client.id}
                          type="button"
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                          onClick={() => handleSelectClient(client)}
                        >
                          <div>
                            <p className="font-medium text-gray-800">{client.nombre}</p>
                            {client.telefono && (
                              <p className="text-xs text-gray-400">{client.telefono}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle de autocompletado */}
              {clienteSeleccionado && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <button
                    type="button"
                    onClick={toggleAutocompletar}
                    className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                  >
                    {autocompletar ? (
                      <CheckSquare size={18} className="text-blue-600" />
                    ) : (
                      <Square size={18} className="text-blue-400" />
                    )}
                    {autocompletar ? 'Autocompletar datos activado' : 'Autocompletar datos desactivado'}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
                  <Input
                    {...register('clienteNombre')}
                    placeholder="Nombre del cliente"
                    error={errors.clienteNombre?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <Input
                    {...register('clienteTelefono')}
                    placeholder="+54 9 11 1234-5678"
                    error={errors.clienteTelefono?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input
                    {...register('clienteEmail')}
                    type="email"
                    placeholder="cliente@email.com"
                    error={errors.clienteEmail?.message}
                    disabled={isFieldDisabled}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {clienteSeleccionado?.email && autocompletar
                      ? 'Email cargado automáticamente'
                      : 'Ingresa el email para confirmaciones'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <Input
                    {...register('clienteDireccion')}
                    placeholder="Dirección completa"
                    error={errors.clienteDireccion?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                  <Input
                    {...register('clienteDocumento')}
                    placeholder="DNI / CUIL"
                    error={errors.clienteDocumento?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localidad</label>
                  <Input
                    {...register('clienteLocalidad')}
                    placeholder="Localidad"
                    error={errors.clienteLocalidad?.message}
                    disabled={isFieldDisabled}
                  />
                </div>
              </div>
            </Card>

            {/* Productos */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Package size={20} className="text-blue-500" />
                Productos
              </h2>

              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Buscar producto..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                      if (selectedProduct && e.target.value !== selectedProduct.nombre) {
                        setSelectedProduct(null);
                      }
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="pl-10 pr-10"
                  />

                  {selectedProduct && (
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Quitar producto seleccionado"
                    >
                      <X size={16} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {showDropdown && searchTerm && filteredProducts.length > 0 && (
                    <div
                      className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl"
                      style={{
                        maxHeight: '200px',
                        height: 'auto',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollBehavior: 'smooth'
                      }}
                    >
                      {filteredProducts.map((product: any) => {
                        const truncateDescription = (text: string) => {
                          if (!text) return '';
                          if (text.length <= 25) return text;
                          return text.substring(0, 25) + '........';
                        };

                        return (
                          <button
                            key={product.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-gray-100 last:border-0"
                            onClick={() => handleSelectProduct(product)}
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <p className="font-medium text-gray-800 truncate">{product.nombre}</p>
                              {product.descripcion && (
                                <p className="text-xs text-gray-400 truncate">
                                  {truncateDescription(product.descripcion)}
                                </p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-blue-600 flex-shrink-0">
                              {formatCurrency(product.precio)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {showDropdown && searchTerm && loadingProducts && (
                    <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      Buscando productos...
                    </div>
                  )}

                  {showDropdown && searchTerm && !loadingProducts && filteredProducts.length === 0 && (
                    <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      No se encontraron productos
                    </div>
                  )}
                </div>

                <div className="w-24">
                  <Input
                    type="number"
                    value={cantidad}
                    onChange={(e) => setCantidad(Number(e.target.value) || 0)}
                    min={1}
                  />
                </div>
                <Button type="button" onClick={handleAddProduct} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus size={18} />
                  Agregar
                </Button>
              </div>

              <div className="space-y-2">
                {productosSeleccionados.map((producto, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{producto.nombre}</p>
                      <p className="text-sm text-gray-500">{formatCurrency(producto.precio)} x {producto.cantidad}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20">
                        <Input
                          type="number"
                          value={producto.cantidad}
                          onChange={(e) => handleCantidadChange(index, Number(e.target.value) || 0)}
                          min={1}
                          className="text-center"
                        />
                      </div>
                      <p className="font-semibold text-gray-800 min-w-[80px] text-right">
                        {formatCurrency(producto.subtotal)}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                ))}

                {productosSeleccionados.length === 0 && (
                  <p className="text-center text-gray-400 py-4">No hay productos agregados</p>
                )}
              </div>
            </Card>
          </div>

          {/* Columna derecha */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <DollarSign size={20} className="text-green-500" />
                Resumen
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>

                {descuento > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento ({descuento}%)</span>
                    <span>-{formatCurrency(subtotal * (descuento / 100))}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descuento (%)</label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    {...register('descuento', { valueAsNumber: true })}
                    placeholder="0"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setValue('descuento', Math.min(Math.max(val, 0), 100));
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Porcentaje de descuento sobre el subtotal</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Costo de Envío ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    {...register('costoEnvio', { valueAsNumber: true })}
                    placeholder="0"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setValue('costoEnvio', val);
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comisión (%)</label>
                  <Input
                    type="number"
                    step="0.01"
                    {...register('comision', { valueAsNumber: true })}
                    placeholder="0"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setValue('comision', Math.min(Math.max(val, 0), 100));
                    }}
                  />
                </div>

                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency(subtotal - (subtotal * (descuento / 100)) + costoEnvio + (subtotal * (comision / 100)))}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Datos de la Venta</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
                  <select
                    {...register('metodoPago')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="cuotas">Cuotas</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    {...register('estado')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="completada">Completada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                  <textarea
                    {...register('observaciones')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Observaciones adicionales..."
                  />
                </div>
              </div>
            </Card>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <Save size={18} />
                {isEditing ? 'Actualizar' : 'Crear'} Venta
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/ventas')} className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}